import type Phaser from "phaser";
import { ResolutionGovernor } from "./resolution";

/** Rendering above 2× costs a lot of fill-rate for little visible gain (Gravity, measured). */
export const MAX_PIXEL_RATIO = 2;

let highDpi = true;
let reapply: (() => void) | null = null;

/** Canvas pixels per CSS pixel. Sizes meant in CSS pixels (fonts, minimum glow) multiply by it. */
export function pixelRatio(): number {
	if (!highDpi) return 1;
	return Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_PIXEL_RATIO);
}

/** Canvas size in device pixels for a container measured in CSS pixels. */
export function canvasSize(cssWidth: number, cssHeight: number, ratio: number) {
	return {
		width: Math.max(1, Math.round(cssWidth * ratio)),
		height: Math.max(1, Math.round(cssHeight * ratio)),
	};
}

/**
 * Keeps the canvas sharp on high-DPI screens (I-006, as in Gravity's display.ts). Phaser's RESIZE
 * mode maps canvas pixels 1:1 to CSS pixels, which is soft on phones, so the game runs in scale
 * mode NONE at CSS size × pixel ratio and is shown at zoom 1/ratio. Re-applied when the
 * container resizes (rotation, fullscreen) and when the page moves to another screen.
 */
export function keepCanvasSharp(game: Phaser.Game, container: HTMLElement): () => void {
	let media: MediaQueryList | undefined;

	const apply = () => {
		const ratio = pixelRatio();
		const cssW = container.clientWidth;
		const cssH = container.clientHeight;
		const { width, height } = canvasSize(cssW, cssH, ratio);
		game.scale.setZoom(1 / ratio);
		game.scale.resize(width, height);
		game.canvas.style.width = `${cssW}px`;
		game.canvas.style.height = `${cssH}px`;
		media?.removeEventListener("change", apply);
		media = window.matchMedia(`(resolution: ${window.devicePixelRatio || ratio}dppx)`);
		media.addEventListener("change", apply);
	};

	const observer = new ResizeObserver(apply);
	observer.observe(container);
	reapply = apply;
	apply();

	return () => {
		reapply = null;
		observer.disconnect();
		media?.removeEventListener("change", apply);
	};
}

/**
 * Drops to 1× density once frames stay slow (resolution.ts), marked on
 * body[data-resolution="reduced"]. The scene re-lays out on the resize this causes.
 */
export function watchFrameRate(game: Phaser.Game): void {
	const governor = new ResolutionGovernor();
	game.events.on("step", (_time: number, delta: number) => {
		if (!governor.frame(delta) || pixelRatio() <= 1) return;
		highDpi = false;
		document.body.dataset.resolution = "reduced";
		reapply?.();
	});
}
